/**
 * Publishes the two GA4 custom customer events the Ceres web pixel subscribes
 * to: `ceres_view_item` and `ceres_view_item_list`. Nothing in the theme
 * published them before, so the pixel — which guards both payloads and drops
 * anything malformed — fired nothing at all.
 *
 * Payload shapes follow the Cotton On group GA4 web data-layer spec. The
 * custom item parameters (`total_sizes`, `total_sizes_available`,
 * `sizes_available`, `size_selected`, `color`, `modal`) are the reason a custom
 * event exists: Shopify's standard `product_viewed` payload cannot carry them.
 * This module deliberately leaves `product_viewed` and `collection_viewed`
 * alone, and is the only publisher of `ceres_view_item`, so the pixel keeps
 * counting each view once.
 *
 * Product-page payloads are rendered server side by `snippets/ga4-view-item.liquid`
 * and read from the DOM here. The collection and search grids are drawn by the
 * Bloomreach Loomi widget, which exposes no render callback, so their items are
 * read off the rendered cards — see `docs/ga4-custom-events.md`.
 */

import { StandardEvents } from '@shopify/events';

const VIEW_ITEM_EVENT = 'ceres_view_item';
const VIEW_ITEM_LIST_EVENT = 'ceres_view_item_list';
const ADD_TO_CART_EVENT = 'ceres_add_to_cart';

/** Surface labels, from the spec's `modal` parameter. */
const MODAL_PDP = 'PDP';
const MODAL_PDP_COLOUR_CHANGE = 'PDP Color Change';
const MODAL_QUICK_VIEW = 'Quick View Modal';
const MODAL_ADD_TO_CART = 'Add To Cart';

/**
 * Loomi selectors, kept identical to `assets/bloomreach-load-more.js` so both
 * bridges agree on what a card is. OBSERVED (live, 2 Sep 2026, recorded there):
 * the light-DOM `br-product-card` host often carries no attributes, and the id
 * lands in its shadow root once the widget has rendered that card.
 */
const WIDGET_HOST_SELECTOR = '.br-widget-host--collections, .br-widget-host--search';
const GRID_SELECTOR = '.br-products__grid';
const CARD_SELECTOR = 'br-product-card';

/** Re-scan delays after a grid mutation, in ms. Cards render into their shadow
 * roots after the host lands in the light DOM, and a light-DOM observer cannot
 * see that, so the scan is repeated rather than trusted first time. */
const RESCAN_DELAYS = [150, 400, 900, 1800, 3000];

/** How long to wait for Shopify's analytics runtime, in ms. */
const PUBLISHER_TIMEOUT = 15000;
const PUBLISHER_POLL = 100;

/** @typedef {Record<string, unknown>} Payload */

/** @type {{ name: string, payload: Payload }[]} */
const pending = [];
let publisherReady = false;

/**
 * Resolves Shopify's theme-side event publisher.
 * @returns {((name: string, payload: Payload) => void) | null} The publisher, or null until the runtime loads.
 */
function resolvePublisher() {
  const analytics = /** @type {{ publish?: unknown } | undefined} */ (
    /** @type {any} */ (window).Shopify?.analytics
  );
  if (typeof analytics?.publish !== 'function') return null;
  return /** @type {(name: string, payload: Payload) => void} */ (analytics.publish.bind(analytics));
}

/**
 * Publishes an event, queueing it until Shopify's analytics runtime exists.
 *
 * `Shopify.analytics` is created by `content_for_header`, which the theme
 * renders before this module runs, but the runtime is not guaranteed to have
 * finished initialising — so a first view is queued rather than dropped.
 *
 * @param {string} name - Custom event name.
 * @param {Payload} payload - Event data, delivered to subscribers as `event.customData`.
 * @returns {void}
 */
function publish(name, payload) {
  pending.push({ name, payload });
  flush();
}

/**
 * Drains the queue once the publisher is available.
 * @returns {void}
 */
function flush() {
  const publisher = resolvePublisher();
  if (!publisher) return;
  publisherReady = true;
  while (pending.length > 0) {
    const event = pending.shift();
    if (event) publisher(event.name, event.payload);
  }
}

/**
 * Polls for the analytics runtime so queued events are not stranded when the
 * first view is published before it loads. Gives up rather than polling
 * forever on a page where the runtime never arrives.
 * @returns {void}
 */
function awaitPublisher() {
  const deadline = Date.now() + PUBLISHER_TIMEOUT;
  const timer = window.setInterval(() => {
    flush();
    if (publisherReady || Date.now() > deadline) window.clearInterval(timer);
  }, PUBLISHER_POLL);
}

/**
 * Reads and parses an inert JSON script tag.
 * @param {Element | null | undefined} script - A `script[type="application/json"]` element.
 * @returns {Payload | null} The parsed object, or null when absent or malformed.
 */
function readJson(script) {
  if (!script?.textContent) return null;
  try {
    const parsed = JSON.parse(script.textContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** @returns {Payload} The page context rendered by `snippets/ga4-analytics.liquid`. */
function context() {
  return readJson(document.querySelector('[data-ga4-context]')) ?? {};
}

/**
 * Identifies a product payload, so a re-render that changes nothing does not
 * republish and a combined-listing colour change does.
 * @param {Payload | null} payload - A `view_item` payload.
 * @returns {string} A stable signature, empty when the payload has no item.
 */
function signatureOf(payload) {
  const items = /** @type {any} */ (payload)?.ecommerce?.items;
  const item = Array.isArray(items) ? items[0] : null;
  if (!item) return '';
  return [item.item_id, item.item_variant, item.price].join('|');
}

/**
 * Publishes a `view_item` from a rendered payload.
 *
 * The pixel requires a currency and exactly one item, and silently drops
 * anything else, so both are checked here rather than sending a payload that
 * will be thrown away.
 *
 * @param {Element | null} script - The `[data-ga4-view-item]` script tag.
 * @param {string} modal - Surface label for the spec's `modal` parameter.
 * @returns {string} The signature published, empty when nothing was.
 */
function publishViewItem(script, modal) {
  const payload = /** @type {any} */ (readJson(script));
  const items = payload?.ecommerce?.items;
  if (!payload?.ecommerce?.currency || !Array.isArray(items) || items.length !== 1) return '';

  items[0].modal = modal;
  publish(VIEW_ITEM_EVENT, payload);
  return signatureOf(payload);
}

/**
 * Watches the product page for the three surfaces a `view_item` fires on: the
 * page itself, a combined-listing colour swatch (which morphs `<main>`, so the
 * payload is replaced in place) and the quick-add modal.
 * @returns {void}
 */
function watchProductViews() {
  const main = document.querySelector('main');
  const dialog = document.getElementById('quick-add-dialog');

  /** @param {ParentNode} root @returns {Element | null} */
  const payloadIn = (root) => root.querySelector('[data-ga4-view-item]');

  let lastSignature = main ? publishViewItem(payloadIn(main), MODAL_PDP) : '';

  if (main) {
    const observer = new MutationObserver(() => {
      const script = payloadIn(main);
      const signature = signatureOf(readJson(script));
      if (!signature || signature === lastSignature) return;
      lastSignature = publishViewItem(script, MODAL_PDP_COLOUR_CHANGE) || lastSignature;
    });
    observer.observe(main, { childList: true, subtree: true, characterData: true });
  }

  // The modal fires per open, not per payload change, so reopening the same
  // product counts again — the shopper viewed it again. Its content is cloned
  // in before the dialog opens, so the payload is already there.
  dialog?.addEventListener('dialog:open', () => publishViewItem(payloadIn(dialog), MODAL_QUICK_VIEW));
}

/**
 * Publishes `ceres_add_to_cart` when the theme adds a line to the cart.
 *
 * The pixel can bridge Shopify's own `product_added_to_cart` instead, but that
 * payload carries no metafields, so its item reports the Shopify product id and
 * no colour. A theme event wins the pixel's claim window, and is the only way
 * `item_id`, `item_variant`, `color` and `size_selected` reach GA4 with the
 * values the spec asks for.
 *
 * `product-form.js` dispatches `CartLinesUpdateEvent` on the form, and every
 * standard event bubbles, so one listener on the document covers the product
 * page, the quick-add modal and the cart upsells alike.
 *
 * @returns {void}
 */
function watchAddToCart() {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const detail = /** @type {any} */ (event);
    if (detail.action !== 'add' || !Array.isArray(detail.lines)) return;

    const payload = /** @type {any} */ (readJson(payloadFor(event.target)));
    const source = payload?.ecommerce?.items?.[0];
    if (!source) return;

    const items = [];
    let value = 0;

    for (const line of detail.lines) {
      const variantId = idOf(line?.merchandiseId);
      const variant = payload.variants?.[variantId];
      const quantity = Number(line?.quantity) || 1;
      // The line's own price when the map knows this variant, because the
      // payload describes whichever variant Liquid rendered, not this one.
      const price = typeof variant?.price === 'number' ? variant.price : Number(source.price) || 0;

      items.push({
        ...source,
        size_selected: variant?.size ?? source.size_selected ?? '',
        price,
        quantity,
        modal: MODAL_ADD_TO_CART,
      });
      value += price * quantity;
    }

    if (items.length === 0) return;

    publish(ADD_TO_CART_EVENT, {
      ecommerce: {
        currency: payload.ecommerce?.currency,
        value: Math.round(value * 100) / 100,
        items,
      },
      refs: detail.lines.map((/** @type {any} */ line) => ({
        product_id: payload.refs?.[0]?.product_id ?? '',
        variant_id: idOf(line?.merchandiseId),
        sku: payload.variants?.[idOf(line?.merchandiseId)]?.sku ?? '',
      })),
      user: payload.user ?? context().user,
    });
  });
}

/**
 * Finds the rendered payload that describes the product a cart event came from.
 *
 * The quick-add modal and a product page can both be present, so the search
 * starts at the element that dispatched the event and walks out to its own
 * `[data-product-grid-content]` before falling back to the page's only payload.
 *
 * @param {EventTarget | null} target - What dispatched the cart event.
 * @returns {Element | null} The payload script, or null when none is in scope.
 */
function payloadFor(target) {
  const element = target instanceof Element ? target : null;
  const scope = element?.closest('[data-product-grid-content]');
  return scope?.querySelector('[data-ga4-view-item]') ?? document.querySelector('[data-ga4-view-item]');
}

/**
 * Reduces a merchandise id to its numeric part.
 * `CartLinesUpdateEvent` carries whatever the form held, which is the bare id,
 * while a Storefront API gid looks like `gid://shopify/ProductVariant/123`.
 * The `variants` map is keyed by the bare id either way.
 * @param {unknown} merchandiseId - The line's merchandise id.
 * @returns {string} The id, empty when there was none.
 */
function idOf(merchandiseId) {
  const raw = String(merchandiseId ?? '').trim();
  if (!raw) return '';
  const lastSegment = raw.split('/').pop();
  return lastSegment ?? '';
}

/**
 * Whether the Loomi widget draws this page's product grid.
 *
 * `snippets/product-grid.liquid` hides the theme's own grid and pagination
 * whenever a later section hosts the widget, so the widget being on the page —
 * not whether it managed to render — is what decides who draws the list. The
 * same condition decides who reports it, and both read the same selector so
 * the stylesheet and the publisher cannot drift apart.
 *
 * @returns {boolean} True when a Loomi collections or search widget is present.
 */
function loomiOwnsGrid() {
  return document.querySelector(WIDGET_HOST_SELECTOR) !== null;
}

/**
 * The spec's seven filter keys, none of them applied.
 *
 * Loomi facets the collection and search grids itself and Shopify never sees
 * the selection, so the values cannot be reported — but the shape can, and a
 * list then carries the same payload shape whichever grid drew it. Matches
 * `snippets/ga4-filters.liquid`, down to `sale` being null until it applies.
 *
 * @returns {Payload} An unselected `filters` object.
 */
function noFilters() {
  return { categories: '', price: '', brand: '', sale: null, color: '', gender: '', size: '' };
}

/**
 * Publishes a `view_item_list` for every Liquid-rendered list on the page, and
 * for each one an infinite-scroll page or a section re-render appends later.
 * Each tag is its own list, which is why each one restarts `index` at zero;
 * a later page carries its own offset, so it continues the count instead.
 * @returns {void}
 */
function publishLiquidLists() {
  // OBSERVED (staging preview, 16 Sep 2026): on a Loomi collection page both
  // publishers ran, so every collection view reported its list twice — once
  // from this hidden grid and once from the widget's. The two disagreed on
  // every identifier, because the hidden grid describes products nobody saw.
  // The widget owns that page; `watchLoomiGrid` reports it.
  if (loomiOwnsGrid()) return;

  /** @type {WeakSet<Element>} Tags already reported, so a re-render is not a second view. */
  const published = new WeakSet();

  const scan = () => {
    for (const script of document.querySelectorAll('[data-ga4-item-list]')) {
      if (published.has(script)) continue;
      published.add(script);
      const payload = /** @type {any} */ (readJson(script));
      const items = payload?.ecommerce?.items;
      if (!payload?.ecommerce?.item_list_name || !Array.isArray(items) || items.length === 0) continue;
      publish(VIEW_ITEM_LIST_EVENT, payload);
    }
  };

  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
}

/**
 * Reads a number that may arrive as a formatted string.
 * @param {unknown} value - Raw feed or DOM value.
 * @returns {number | null} The number, or null when it cannot be read.
 */
function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  // Currency text: keep digits, a decimal point and a sign, drop symbols and
  // thousands separators. "A$39.99" -> 39.99.
  const cleaned = value.replace(/[^0-9.\-]/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Reads a Loomi card's raw Discovery document.
 *
 * The widget holds the feed document on the custom element as a `product`
 * property — the same property `assets/bloomreach-recommendations-tiles.js`
 * sets when it constructs an extra card. It carries the fields the widget's
 * `fl_fields` setting requested, which is a far better source than the card's
 * rendered text.
 *
 * @param {Element} card - A `br-product-card` element.
 * @returns {Record<string, any> | null} The feed document, or null when unavailable.
 */
function feedDocumentOf(card) {
  const doc = /** @type {any} */ (card).product;
  return doc && typeof doc === 'object' ? doc : null;
}

/**
 * Reads a Loomi card's product id, preferring the light-DOM host and falling
 * back to the shadow root, exactly as `assets/bloomreach-load-more.js` does.
 * @param {Element} card - A `br-product-card` element.
 * @returns {string} The identifier, empty while the card is unrendered.
 */
function cardIdOf(card) {
  const lightId = card.getAttribute('data-pid')?.trim();
  if (lightId) return lightId;

  const shadowRoot = card.shadowRoot;
  const shadowId = shadowRoot?.querySelector('[data-pid]')?.getAttribute('data-pid')?.trim();
  if (shadowId) return shadowId;

  return shadowRoot?.querySelector('[data-product-url]')?.getAttribute('data-product-url')?.trim() ?? '';
}

/**
 * Reads the first non-empty string among a feed document's fields.
 * @param {Record<string, any> | null} doc - The feed document.
 * @param {string[]} names - Field names, best first.
 * @returns {string} The value, empty when the document carries none of them.
 */
function feedField(doc, names) {
  for (const name of names) {
    const value = doc?.[name];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

/**
 * Builds one GA4 item from a Loomi card.
 *
 * The identifiers match `snippets/ga4-item.liquid`, so a product reports the
 * same `item_id`, `item_variant` and `color` whether it was seen in a grid or
 * on its own page: the style reference, the style-colour reference and the
 * refinement colour. Those three reach the card only if the widget asks for
 * `style_ref`, `item_colour_ref` and `color_group` in its `fl_fields` — see
 * `docs/ga4-custom-events.md` for the Data Hub and widget settings.
 *
 * Each falls back the way the PDP falls back, so a widget that has not been
 * reconfigured yet keeps reporting what it reported before rather than
 * dropping the field: `item_id` to the Shopify product id the feed calls
 * `pid`, and `color` to the sku-level colour attributes.
 *
 * Taxonomy categories are still absent — they are not mapped in Data Hub at
 * all, which is a larger job than adding a requested field.
 *
 * @param {Element} card - A `br-product-card` element.
 * @param {number} index - Zero-based position in the list.
 * @param {{ item_list_name?: string, item_list_id?: string, modal?: string }} list - List identity.
 * @returns {Payload | null} The item, or null when the card has not rendered enough to describe.
 */
function itemFromCard(card, index, list) {
  const id = cardIdOf(card);
  const doc = feedDocumentOf(card);

  const itemId = feedField(doc, ['style_ref']) || String(doc?.pid ?? id ?? '').trim();
  // `title` carries the colourway the product page also reports — "Phoebe
  // Premium Tee - Black". `style` is the style-level name without it, so it
  // would report a different item_name for the same product.
  const itemName = String(doc?.title ?? doc?.style ?? cardTitleOf(card) ?? '').trim();
  if (!itemId || !itemName) return null;

  const listPrice = toNumber(doc?.price ?? doc?.listPrice);
  const salePrice = toNumber(doc?.sale_price ?? doc?.salePrice);
  const domPrice = doc ? null : cardPriceOf(card);
  const price = salePrice ?? listPrice ?? domPrice ?? 0;
  const discount = listPrice !== null && salePrice !== null && listPrice > salePrice ? listPrice - salePrice : 0;

  return {
    item_id: itemId,
    item_name: itemName,
    discount: Math.round(discount * 100) / 100,
    index,
    item_brand: String(doc?.brand ?? '').trim(),
    item_category: '',
    item_category2: '',
    item_category3: '',
    item_category4: '',
    item_category5: '',
    item_list_name: list.item_list_name ?? '',
    item_list_id: list.item_list_id ?? '',
    item_variant: feedField(doc, ['item_colour_ref']),
    price: Math.round(price * 100) / 100,
    color: feedField(doc, ['color_group', 'sku_color_group', 'sku_color']),
    modal: list.modal ?? '',
  };
}

/**
 * Reads a card's rendered title, for the case where the feed document is not
 * exposed on the element.
 * @param {Element} card - A `br-product-card` element.
 * @returns {string} The title, empty when the card has not rendered.
 */
function cardTitleOf(card) {
  const root = card.shadowRoot ?? card;
  return root.querySelector('.ceres-card__title')?.textContent?.trim() ?? '';
}

/**
 * Reads a card's rendered selling price, for the case where the feed document
 * is not exposed on the element. The selling price is the sale price when the
 * card shows one, matching the spec's "price after discount".
 * @param {Element} card - A `br-product-card` element.
 * @returns {number | null} The price, or null when it cannot be read.
 */
function cardPriceOf(card) {
  const root = card.shadowRoot ?? card;
  const text = root.querySelector('.price-item--sale, .price')?.textContent;
  return toNumber(text ?? '');
}

/**
 * Watches the Loomi grid and publishes a `view_item_list` for each batch of
 * cards it renders.
 *
 * Bloomreach Discovery exposes no render-complete callback, so the grid is
 * observed the same way `assets/bloomreach-load-more.js` observes it, and the
 * scan is repeated because a card's content lands in its shadow root where a
 * light-DOM observer cannot see it.
 *
 * Load-more appends to the same list, so `index` keeps counting rather than
 * restarting — the spec restarts it per list, not per page.
 *
 * @returns {void}
 */
function watchLoomiGrid() {
  const list = /** @type {any} */ (context()).list;
  if (!list?.item_list_name) return;

  const host = document.querySelector(WIDGET_HOST_SELECTOR);
  if (!host) return;

  /** @type {Set<string>} Cards already reported, so a re-render is not a second view. */
  const seen = new Set();
  let nextIndex = 0;
  /** @type {number[]} */
  let timers = [];

  const scan = () => {
    const grid = host.querySelector(GRID_SELECTOR);
    if (!grid) return;

    /** @type {Payload[]} */
    const items = [];
    for (const card of grid.querySelectorAll(CARD_SELECTOR)) {
      const id = cardIdOf(card);
      if (!id || seen.has(id)) continue;
      const item = itemFromCard(card, nextIndex, list);
      if (!item) continue;
      seen.add(id);
      nextIndex += 1;
      items.push(item);
    }

    if (items.length === 0) return;

    publish(VIEW_ITEM_LIST_EVENT, {
      ecommerce: {
        sort: sortOf(list),
        item_list_name: list.item_list_name,
        item_list_id: list.item_list_id ?? '',
        filters: noFilters(),
        items,
      },
      user: /** @type {any} */ (context()).user ?? { id: '', loginState: false },
    });
  };

  const scheduleScan = () => {
    for (const timer of timers) window.clearTimeout(timer);
    timers = RESCAN_DELAYS.map((delay) => window.setTimeout(scan, delay));
  };

  scheduleScan();
  new MutationObserver(scheduleScan).observe(host, { childList: true, subtree: true });
}

/**
 * Resolves the active sort. The Loomi widget writes its sort into the URL, so
 * that wins over the Liquid fallback, which only knows Shopify's own sort.
 * @param {{ sort?: string }} list - List context from `snippets/ga4-analytics.liquid`.
 * @returns {string} The sort key, empty when none is active.
 */
function sortOf(list) {
  const fromUrl = new URLSearchParams(window.location.search).get('sort');
  return (fromUrl || list.sort || '').trim();
}

/**
 * @returns {void}
 */
function init() {
  awaitPublisher();
  watchProductViews();
  watchAddToCart();
  publishLiquidLists();
  watchLoomiGrid();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
